// src/components/mensagens/MessageBubble.jsx
// ============================================================================
// 🎯 COMPONENTE: MessageBubble — V1 ENTERPRISE
// 📋 Função: Bolha de mensagem individual no histórico de conversa
// 📱 Responsivo: Swipe actions em mobile · Click direito em desktop
// 🎨 Design: Glassmorphism · Animações GPU-accelerated · Estados visuais
// 🔒 Robusto: Anti-crash · Permissões granulares · Acessibilidade total
// ============================================================================
// 🎮 Interações:
//    👆 Click / Tap        → Navega para detalhes da mensagem
//    🖱️ Click direito       → Menu contextual (desktop)
//    👆 Long press          → Menu contextual (mobile)
//    👈 Swipe left (mobile) → Revela ações rápidas
// ============================================================================

import React, {
  useState,
  useCallback,
  useRef,
  memo,
} from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Check,
  CheckCheck,
  AlertCircle,
  Clock,
  MoreHorizontal,
  Eye,
  Edit3,
  Forward,
  Copy,
  Trash2,
} from 'lucide-react';
import { useIsMobile } from '../../hooks/useMediaQuery';
import {
  truncateText,
  formatDateTime,
  getMessageActionPermissions,
} from './helpers';

// ============================================================================
// 🎨 CONSTANTES
// ============================================================================
const SWIPE_THRESHOLD = 80; // px para ativar ação no swipe
const LONG_PRESS_DURATION = 500; // ms para ativar long press
const SWIPE_ACTIONS_VISIBLE_WIDTH = 160; // px visíveis das ações no swipe

// ============================================================================
// 🎯 HOOK: useSwipeActions (mobile)
// ============================================================================
function useSwipeActions(onSwipeLeft, enabled = true) {
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [isSwiping, setIsSwiping] = useState(false);
  const startX = useRef(0);
  const currentX = useRef(0);
  const rafRef = useRef(null);

  const handleTouchStart = useCallback(
    (e) => {
      if (!enabled) return;
      startX.current = e.touches[0].clientX;
      currentX.current = startX.current;
      setIsSwiping(true);
    },
    [enabled]
  );

  const handleTouchMove = useCallback(
    (e) => {
      if (!isSwiping || !enabled) return;

      currentX.current = e.touches[0].clientX;
      const diff = startX.current - currentX.current;

      // Só permite swipe para esquerda
      if (diff > 0) {
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
        rafRef.current = requestAnimationFrame(() => {
          setSwipeOffset(Math.min(diff, SWIPE_ACTIONS_VISIBLE_WIDTH));
        });
      }
    },
    [isSwiping, enabled]
  );

  const handleTouchEnd = useCallback(() => {
    if (!isSwiping) return;

    const diff = startX.current - currentX.current;

    if (diff > SWIPE_THRESHOLD) {
      setSwipeOffset(SWIPE_ACTIONS_VISIBLE_WIDTH);
      onSwipeLeft?.();
    } else {
      setSwipeOffset(0);
    }

    setIsSwiping(false);
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
  }, [isSwiping, onSwipeLeft]);

  const resetSwipe = useCallback(() => {
    setSwipeOffset(0);
  }, []);

  return {
    swipeOffset,
    isSwiping,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    resetSwipe,
  };
}

// ============================================================================
// 🎯 HOOK: useLongPress (mobile)
// ============================================================================
function useLongPress(onLongPress, delay = LONG_PRESS_DURATION) {
  const timerRef = useRef(null);
  const [isLongPressing, setIsLongPressing] = useState(false);

  const start = useCallback(
    (e) => {
      setIsLongPressing(true);
      timerRef.current = setTimeout(() => {
        onLongPress?.(e);
        setIsLongPressing(false);
      }, delay);
    },
    [onLongPress, delay]
  );

  const cancel = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setIsLongPressing(false);
  }, []);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  return {
    onTouchStart: start,
    onTouchEnd: cancel,
    onTouchMove: cancel,
    onTouchCancel: cancel,
    isLongPressing,
  };
}

// ============================================================================
// 🎯 SUBCOMPONENTE: StatusIcon (memoizado)
// ============================================================================
const StatusIcon = memo(({ status, isOutgoing, size = 14 }) => {
  if (!isOutgoing) return null;

  const iconProps = { size, className: 'flex-shrink-0' };

  switch (status) {
    case 'read':
      return <CheckCheck {...iconProps} className="text-blue-400" />;
    case 'delivered':
      return <CheckCheck {...iconProps} className="text-gray-400" />;
    case 'sent':
      return <Check {...iconProps} className="text-gray-400" />;
    case 'failed':
      return <AlertCircle {...iconProps} className="text-red-400" />;
    case 'sending':
      return <Clock {...iconProps} className="text-gray-400 animate-pulse" />;
    default:
      return <Check {...iconProps} className="text-gray-400" />;
  }
});
StatusIcon.displayName = 'StatusIcon';

// ============================================================================
// 🎯 SUBCOMPONENTE: SwipeActions (mobile)
// ============================================================================
const SwipeActions = memo(({ permissions, onView, onEdit, onCopy, onDelete }) => {
  const actions = [];

  if (permissions.canView) {
    actions.push({
      id: 'view',
      icon: Eye,
      color: 'bg-blue-500',
      label: 'Ver',
      onClick: onView,
    });
  }
  if (permissions.canEdit) {
    actions.push({
      id: 'edit',
      icon: Edit3,
      color: 'bg-indigo-500',
      label: 'Editar',
      onClick: onEdit,
    });
  }
  if (permissions.canCopy) {
    actions.push({
      id: 'copy',
      icon: Copy,
      color: 'bg-purple-500',
      label: 'Copiar',
      onClick: onCopy,
    });
  }
  if (permissions.canDelete) {
    actions.push({
      id: 'delete',
      icon: Trash2,
      color: 'bg-red-500',
      label: 'Excluir',
      onClick: onDelete,
    });
  }

  return (
    <div
      className="absolute top-0 right-0 h-full flex items-stretch"
      style={{ width: SWIPE_ACTIONS_VISIBLE_WIDTH }}
    >
      {actions.map((action) => (
        <motion.button
          key={action.id}
          whileTap={{ scale: 0.9 }}
          onClick={(e) => {
            e.stopPropagation();
            action.onClick();
          }}
          className={`
            flex-1 flex flex-col items-center justify-center gap-0.5
            ${action.color} text-white
            transition-all active:brightness-110
          `}
          style={{ contain: 'layout style paint' }}
          aria-label={action.label}
        >
          <action.icon size={16} />
          <span className="text-[9px] font-semibold">{action.label}</span>
        </motion.button>
      ))}
    </div>
  );
});
SwipeActions.displayName = 'SwipeActions';

// ============================================================================
// 🎯 COMPONENTE PRINCIPAL
// ============================================================================
function MessageBubble({
  message = null,
  index = 0,
  clientName = 'Cliente',
  isMobile = false,
  onView,
  onEdit,
  onResend,
  onCopy,
  onDelete,
  onContextMenu,
}) {
  const navigate = useNavigate();

  // ==========================================================================
  // 📊 ESTADOS
  // ==========================================================================
  const [showActions, setShowActions] = useState(false);
  const [hovered, setHovered] = useState(false);
  const bubbleRef = useRef(null);

  // ==========================================================================
  // 🔒 PERMISSÕES
  // ==========================================================================
  const permissions = getMessageActionPermissions(message);

  // ==========================================================================
  // 📱 SWIPE ACTIONS (mobile)
  // ==========================================================================
  const handleSwipeLeft = useCallback(() => {
    setShowActions(true);
  }, []);

  const swipe = useSwipeActions(handleSwipeLeft, isMobile);

  // ==========================================================================
  // 📱 LONG PRESS (mobile) → Menu contextual
  // ==========================================================================
  const handleLongPressAction = useCallback(() => {
    onContextMenu?.(message, bubbleRef);
  }, [message, onContextMenu]);

  const longPress = useLongPress(handleLongPressAction);

  // ==========================================================================
  // 🎮 HANDLERS
  // ==========================================================================
  const handleClick = useCallback(
    (e) => {
      // Se o swipe está aberto, fecha
      if (showActions && isMobile) {
        setShowActions(false);
        swipe.resetSwipe();
        return;
      }

      // Navega para detalhes
      if (message?.id) {
        navigate(`/mensagens/${message.id}`);
        onView?.(message);
      }
    },
    [message, navigate, onView, showActions, isMobile, swipe]
  );

  const handleContextMenu = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      onContextMenu?.(message, bubbleRef);
    },
    [message, onContextMenu]
  );

  const handleActionClick = useCallback(
    (action) => {
      setShowActions(false);
      swipe.resetSwipe();

      switch (action) {
        case 'view':
          if (message?.id) {
            navigate(`/mensagens/${message.id}`);
          }
          onView?.(message);
          break;
        case 'edit':
          onEdit?.(message);
          break;
        case 'copy':
          onCopy?.(message);
          break;
        case 'delete':
          onDelete?.(message);
          break;
        default:
          break;
      }
    },
    [message, navigate, onView, onEdit, onCopy, onDelete, swipe]
  );

  // ==========================================================================
  // 🎨 CLASSES COMPUTADAS
  // ==========================================================================
  const isOutgoing = message?.direction === 'outgoing';
  const isFailed = message?.status === 'failed';
  const messageText = message?.message || '';
  const timestamp = message?.createdAt || 0;
  const template = message?.template;

  // ==========================================================================
  // ⏸️ EARLY RETURN
  // ==========================================================================
  if (!message) return null;

  // ==========================================================================
  // 🖼️ RENDER
  // ==========================================================================
  return (
    <motion.div
      ref={bubbleRef}
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        duration: 0.2,
        delay: Math.min(index * 0.03, 0.6),
        ease: [0.25, 0.46, 0.45, 0.94],
      }}
      className={`
        relative flex
        ${isOutgoing ? 'justify-end' : 'justify-start'}
        ${isMobile ? 'px-2' : 'px-0'}
        group
        select-none
      `}
      style={{
        willChange: 'transform, opacity',
        contain: 'layout style paint',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => {
        setHovered(false);
        setShowActions(false);
      }}
      {...(isMobile ? longPress : {})}
      onContextMenu={handleContextMenu}
    >
      {/* ================================================================ */}
      {/* 📱 SWIPE BACKGROUND ACTIONS (mobile) */}
      {/* ================================================================ */}
      {isMobile && (
        <SwipeActions
          permissions={permissions}
          onView={() => handleActionClick('view')}
          onEdit={() => handleActionClick('edit')}
          onCopy={() => handleActionClick('copy')}
          onDelete={() => handleActionClick('delete')}
        />
      )}

      {/* ================================================================ */}
      {/* BOLHA PRINCIPAL */}
      {/* ================================================================ */}
      <motion.div
        animate={{
          x: isMobile ? -swipe.swipeOffset : 0,
        }}
        transition={{
          type: 'spring',
          stiffness: 400,
          damping: 30,
        }}
        onClick={handleClick}
        className={`
          relative max-w-[88%] sm:max-w-[75%] md:max-w-[65%]
          rounded-2xl cursor-pointer
          transition-shadow duration-150
          ${isOutgoing
            ? 'bg-gradient-to-br from-emerald-500 to-green-600 text-white rounded-br-md shadow-sm hover:shadow-md'
            : 'bg-white text-gray-800 rounded-bl-md shadow-sm hover:shadow-md border border-gray-100'
          }
          ${isFailed
            ? '!bg-gradient-to-br !from-red-100 !to-red-50 !text-red-800 !border !border-red-200'
            : ''
          }
          ${isMobile ? 'px-3 py-2.5' : 'px-4 py-3'}
        `}
        style={{
          willChange: 'transform',
          contain: 'layout style paint',
        }}
        role="button"
        tabIndex={0}
        aria-label={`Mensagem ${isOutgoing ? 'enviada' : 'recebida'} em ${formatDateTime(timestamp)}`}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleClick(e);
          }
        }}
      >
        {/* Indicador de long press (mobile) */}
        {longPress.isLongPressing && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className={`
              absolute inset-0 rounded-2xl
              ${isOutgoing ? 'bg-white/20' : 'bg-black/5'}
            `}
          />
        )}

        {/* ================================================================ */}
        {/* CONTEÚDO DA MENSAGEM */}
        {/* ================================================================ */}
        <div className="relative">
          {/* Template badge (se não for padrão) */}
          {template && template !== 'padrao' && (
            <div
              className={`
                inline-flex items-center px-2 py-0.5 rounded-full mb-1.5
                text-[10px] font-semibold uppercase tracking-wide
                ${isOutgoing
                  ? 'bg-white/20 text-white/90'
                  : 'bg-gray-100 text-gray-600'
                }
                ${isFailed ? '!bg-red-200 !text-red-700' : ''}
              `}
            >
              {template}
            </div>
          )}

          {/* Texto da mensagem */}
          <p
            className={`
              whitespace-pre-wrap break-words leading-relaxed
              ${isMobile ? 'text-xs' : 'text-sm'}
              ${isFailed ? '!text-red-700' : ''}
            `}
            style={{ wordBreak: 'break-word' }}
          >
            {messageText || (
              <span className={isOutgoing ? 'opacity-60' : 'text-gray-400'}>
                Mensagem vazia
              </span>
            )}
          </p>

          {/* ================================================================ */}
          {/* METADADOS: Hora + Status + Botão de ações */}
          {/* ================================================================ */}
          <div
            className={`
              flex items-center justify-end gap-1.5 mt-1.5
              ${isOutgoing ? 'text-white/70' : 'text-gray-400'}
              ${isFailed ? '!text-red-500' : ''}
            `}
          >
            {/* Hora */}
            <span
              className={`
                ${isMobile ? 'text-[10px]' : 'text-xs'}
                opacity-80
              `}
            >
              {formatDateTime(timestamp)}
            </span>

            {/* Status (apenas outgoing) */}
            <StatusIcon
              status={message.status}
              isOutgoing={isOutgoing}
              size={isMobile ? 12 : 14}
            />

            {/* Botão de mais ações (desktop hover / mobile sempre visível se swipe aberto) */}
            {(!isMobile || showActions) && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onContextMenu?.(message, bubbleRef);
                }}
                className={`
                  p-0.5 rounded-lg transition-colors
                  ${isOutgoing
                    ? 'hover:bg-white/20 text-white/60 hover:text-white'
                    : 'hover:bg-gray-100 text-gray-400 hover:text-gray-600'
                  }
                  ${!isMobile && !hovered && 'opacity-0 group-hover:opacity-100'}
                  transition-opacity duration-150
                `}
                aria-label="Mais ações"
              >
                <MoreHorizontal size={isMobile ? 14 : 16} />
              </button>
            )}
          </div>

          {/* Indicador de falha */}
          {isFailed && (
            <div className="flex items-center gap-1.5 mt-1.5 text-red-500">
              <AlertCircle size={12} />
              <span className="text-[10px] font-medium">Falha no envio</span>
            </div>
          )}
        </div>

        {/* ================================================================ */}
        {/* INDICADOR DE "EDITADO" (se aplicável) */}
        {/* ================================================================ */}
        {message.edited && (
          <span
            className={`
              absolute -bottom-1 right-2
              text-[9px] italic
              ${isOutgoing ? 'text-white/50' : 'text-gray-400'}
            `}
          >
            editada
          </span>
        )}
      </motion.div>
    </motion.div>
  );
}

// ============================================================================
// 🎯 EXPORTAÇÕES
// ============================================================================
MessageBubble.displayName = 'MessageBubble';

export { StatusIcon, SwipeActions };
export default memo(MessageBubble);